import { MIN_TIMER_SECONDS } from "@wmf/shared/practice";
import { PracticeInstrumentPicker, usePracticeInstrument, type PracticeInstrumentChoice } from "../practiceInstrument.js";
import { PracticeToolContextNote } from "../PracticeToolContextNote.js";
import { preferredToolInstrument, readPracticeToolContext } from "../practiceToolContext.js";
import type { MetronomeSubdivisionId } from "@wmf/shared/metronomePractice";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, get, send } from "../api.js";
import type { MetronomePracticePreset } from "../practiceTodos.js";
import {
  createMetronomeLoopWavBytes,
  metronomeAudioPatternsEqual,
  metronomeAudioPosition,
  metronomeBpmAtBar,
  metronomeTrackLayout,
  type MetronomeAudioPattern,
  type MetronomeSpeedUp,
} from "../metronomeAudio.js";
import { PlaybackNumberInput } from "../ui/PlaybackNumberInput.js";
import "../metronome.css";

export type BeatMode = "accent" | "normal" | "muted";

export type TimeSignature = {
  label: MetronomePracticePreset["signature"];
  beats: number;
};

export type SubdivisionId = MetronomeSubdivisionId;

export type Subdivision = {
  id: SubdivisionId;
  label: string;
  shortLabel: string;
  divisions: number;
  positions: readonly number[];
};

export const TIME_SIGNATURES: readonly TimeSignature[] = [
  { label: "2/4", beats: 2 },
  { label: "3/4", beats: 3 },
  { label: "4/4", beats: 4 },
  { label: "5/4", beats: 5 },
  { label: "6/8", beats: 6 },
  { label: "7/8", beats: 7 },
];

export const SUBDIVISIONS: readonly Subdivision[] = [
  {
    id: "quarter",
    label: "Quarter notes",
    shortLabel: "Quarter",
    divisions: 1,
    positions: [0],
  },
  {
    id: "eighth",
    label: "8th notes",
    shortLabel: "8ths",
    divisions: 2,
    positions: [0, 1],
  },
  {
    id: "sixteenth",
    label: "16th notes",
    shortLabel: "16ths",
    divisions: 4,
    positions: [0, 1, 2, 3],
  },
  {
    id: "triplet",
    label: "8th-note triplets",
    shortLabel: "Triplets",
    divisions: 3,
    positions: [0, 1, 2],
  },
  {
    id: "triplet-outer",
    label: "Triplet: first and last",
    shortLabel: "Triplet 1 + 3",
    divisions: 3,
    positions: [0, 2],
  },
  {
    id: "sixteenth-outer",
    label: "16th: first and last",
    shortLabel: "16th 1 + 4",
    divisions: 4,
    positions: [0, 3],
  },
];

const MIN_BPM = 10;
const MAX_BPM = 240;
const DEFAULT_BPM = 80;
const ELAPSED_UI_INTERVAL_MS = 250;
const TAP_RESET_MS = 60_000 / MIN_BPM + 1_000;

type SpeedUpSettings = MetronomeSpeedUp & { enabled: boolean };
function defaultSpeedUp(bpm: number): SpeedUpSettings {
  return { enabled: false, increaseBpm: 2, everyBars: 4, targetBpm: Math.min(MAX_BPM, bpm + 20) };
}

export interface MetronomeSession {
  instrument?: string;
  startedAtMs: number;
  startedAtPerf: number;
  startBpm: number;
  signature: string;
}

interface MetronomeSaveResult {
  clamped: boolean;
  session?: unknown;
}

interface AssignedMetronomeTarget {
  studentId: string;
  todoId: string;
}

export interface PendingMetronomeSave {
  practicePath: string;
  session: MetronomeSession;
  stoppedAtMs: number;
  endBpm: number;
  assigned?: AssignedMetronomeTarget;
  assignmentTitle?: string;
}

interface MetronomeAssignment {
  id: string;
  title: string;
  description: string | null;
  suggestedMinutes: number | null;
  metronome: MetronomePracticePreset | null;
}

export type TempoTerm = {
  name: string;
  description: string;
};

export function metronomeTempoTerm(bpm: number): TempoTerm {
  if (bpm < 20) return { name: "Larghissimo", description: "Extremely slow" };
  if (bpm < 40) return { name: "Grave", description: "Slow and solemn" };
  if (bpm < 60) return { name: "Largo", description: "Broad and slow" };
  if (bpm < 66) return { name: "Larghetto", description: "Rather slow" };
  if (bpm < 76) return { name: "Adagio", description: "Slow and stately" };
  if (bpm < 108) return { name: "Andante", description: "At a walking pace" };
  if (bpm < 120) return { name: "Moderato", description: "Moderately" };
  if (bpm < 168) return { name: "Allegro", description: "Fast and bright" };
  if (bpm < 200) return { name: "Presto", description: "Very fast" };
  return { name: "Prestissimo", description: "Extremely fast" };
}

export function shouldSaveMetronomeSession(
  saveToPractice: boolean,
  measuredSeconds: number,
): boolean {
  return saveToPractice && measuredSeconds >= MIN_TIMER_SECONDS;
}

/**
 * Derive a wall-clock timestamp from a monotonic elapsed-time source. Page
 * suspension/phone lock does not require JavaScript to keep running for
 * `performance.now()` to advance, and system clock corrections cannot make a
 * real practice interval jump backwards or forwards.
 */
function stoppedAtFrom(session: MetronomeSession): number {
  const elapsedMs = Math.max(0, performance.now() - session.startedAtPerf);
  return session.startedAtMs + elapsedMs;
}

/**
 * Assigned metronome sessions are one atomic intent: either the measured
 * interval is saved against the exact assignment that launched it, or the
 * caller gets the failure and can keep that assignment visible for retry.
 *
 * In particular, a 404/409 is not permission to resend the interval to the
 * generic endpoint. The assignment may have become inactive, its preset may be
 * invalid, or the relationship may have been withdrawn. Silently downgrading
 * any of those cases to free practice loses attribution and can also duplicate
 * a write after an ambiguous failure.
 */
export async function saveMetronomeInterval(
  practicePath: string,
  session: MetronomeSession,
  endBpm: number,
  stoppedAtMs: number,
  assigned?: AssignedMetronomeTarget,
): Promise<MetronomeSaveResult> {
  const startedAt = new Date(session.startedAtMs).toISOString();
  const stoppedAt = new Date(stoppedAtMs).toISOString();
  const genericBody = {
    ...(session.instrument?{instrument:session.instrument}:{}),
    name: metronomePracticeName(session.signature, session.startBpm, endBpm),
    startedAt,
    stoppedAt,
  };

  if (assigned) {
    return send<MetronomeSaveResult>(
      "POST",
      `/me/students/${encodeURIComponent(
        assigned.studentId,
      )}/practice-todos/${encodeURIComponent(assigned.todoId)}/metronome`,
      { startedAt, stoppedAt, endBpm, ...(session.instrument?{instrument:session.instrument}:{}) },
    );
  }

  return send<MetronomeSaveResult>(
    "POST",
    `${practicePath}/metronome`,
    genericBody,
  );
}

/**
 * Save exactly the interval captured at stop time. Callers retain this object
 * after a failed request so retry cannot silently use a later BPM, stop time or
 * assignment selection.
 */
export function savePendingMetronomeInterval(
  pending: PendingMetronomeSave,
): Promise<MetronomeSaveResult> {
  return saveMetronomeInterval(
    pending.practicePath,
    pending.session,
    pending.endBpm,
    pending.stoppedAtMs,
    pending.assigned,
  );
}

export function clampMetronomeBpm(value: number): number {
  return Math.min(MAX_BPM, Math.max(MIN_BPM, Math.round(value)));
}

export function metronomePracticeName(
  signature: string,
  startBpm: number,
  endBpm: number,
): string {
  const tempo = startBpm === endBpm ? `${endBpm} BPM` : `${startBpm}→${endBpm} BPM`;
  return `Metronome · ${signature} · ${tempo}`;
}

export function defaultMetronomeBeatModes(beats: number): BeatMode[] {
  return Array.from({ length: beats }, (_, index) =>
    index === 0 ? "accent" : "normal",
  );
}

export function nextMetronomeBeatMode(mode: BeatMode): BeatMode {
  if (mode === "normal") return "accent";
  if (mode === "accent") return "muted";
  return "normal";
}

export function metronomeBpmFromTaps(taps: readonly number[]): number | null {
  if (taps.length < 2) return null;
  const intervals = taps.slice(1).map((tap, index) => tap - taps[index]!);
  if (intervals.some((interval) => !Number.isFinite(interval) || interval <= 0)) {
    return null;
  }
  const average = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
  return clampMetronomeBpm(60_000 / average);
}

export function claimMetronomeStart(
  guard: { current: boolean },
  session: MetronomeSession | null,
): boolean {
  if (guard.current || session !== null) return false;
  guard.current = true;
  return true;
}

export function subdivisionOffsets(subdivision: Subdivision): number[] {
  return subdivision.positions.map((position) => position / subdivision.divisions);
}

export function defaultSubdivisionModes(
  beats: number,
  subdivision: Subdivision,
): BeatMode[][] {
  const extraCount = Math.max(
    0,
    subdivision.positions.filter((position) => position !== 0).length,
  );
  return Array.from({ length: beats }, () =>
    Array.from({ length: extraCount }, () => "normal" as BeatMode),
  );
}

export function isPracticePath(pathname: string): boolean {
  return pathname === "/practice" || pathname.startsWith("/practice/");
}

function beatModeLabel(mode: BeatMode): string {
  if (mode === "accent") return "accented";
  if (mode === "muted") return "silent";
  return "normal";
}

function secondsAsClock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, "0")}`;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest(
      "input, textarea, select, button, summary, [role='button'], " +
      "[tabindex]:not([tabindex='-1']), [contenteditable]:not([contenteditable='false'])",
    ),
  );
}

type ScreenWakeLockSentinel = EventTarget & {
  release: () => Promise<void>;
};

type NavigatorWithMetronomeFeatures = Navigator & {
  wakeLock?: {
    request: (type: "screen") => Promise<ScreenWakeLockSentinel>;
  };
  audioSession?: {
    type: string;
  };
};

type MetronomeContextValue = {
  bpm: number;
  liveBpm: number | null;
  speedUp: SpeedUpSettings;
  changeSpeedUp: (change: Partial<SpeedUpSettings>) => void;
  signature: TimeSignature;
  subdivision: Subdivision;
  beatModes: BeatMode[];
  subdivisionModes: BeatMode[][];
  playing: boolean;
  activeBeat: number | null;
  elapsedSeconds: number;
  canSavePractice: boolean;
  practiceInstrument: PracticeInstrumentChoice;
  saveToPractice: boolean;
  keepScreenAwake: boolean;
  wakeLockSupported: boolean;
  saving: boolean;
  status: string | null;
  problem: string | null;
  pendingSave: PendingMetronomeSave | null;
  tempoTerm: TempoTerm;
  assignment: MetronomeAssignment | null;
  assignmentLoading: boolean;
  changeBpm: (value: number) => void;
  tapTempo: () => void;
  tapCount: number;
  resetTapTempo: () => void;
  chooseSignature: (signature: TimeSignature) => void;
  chooseSubdivision: (subdivision: Subdivision) => void;
  cycleBeat: (index: number) => void;
  cycleSubdivision: (beatIndex: number, extraIndex: number) => void;
  toggle: () => void;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  retrySave: () => Promise<void>;
  discardPendingSave: () => void;
  setSaveToPractice: (value: boolean) => void;
  setKeepScreenAwake: (value: boolean) => void;
};

const MetronomeContext = createContext<MetronomeContextValue | null>(null);

export function useMetronome(): MetronomeContextValue {
  const value = useContext(MetronomeContext);
  if (!value) throw new Error("useMetronome must be used inside MetronomeProvider");
  return value;
}

export function MetronomeProvider({
  studentId,
  onUnmountPendingSave,
  onUnmountPendingSaveCleared,
  children,
}: {
  studentId?: string;
  onUnmountPendingSave?: (pending: PendingMetronomeSave) => void;
  onUnmountPendingSaveCleared?: () => void;
  children: ReactNode;
}) {
  const location = useLocation();
  const practicePath = studentId ? `/me/students/${studentId}/practice` : null;
  const [bpm, setBpm] = useState(DEFAULT_BPM);
  const [liveBpm, setLiveBpm] = useState<number | null>(null);
  const [speedUp, setSpeedUp] = useState(() => defaultSpeedUp(DEFAULT_BPM));
  const [signature, setSignature] = useState<TimeSignature>(TIME_SIGNATURES[2]!);
  const [subdivision, setSubdivision] = useState<Subdivision>(SUBDIVISIONS[0]!);
  const [beatModes, setBeatModes] = useState<BeatMode[]>(() =>
    defaultMetronomeBeatModes(4),
  );
  const [subdivisionModes, setSubdivisionModes] = useState<BeatMode[][]>(() =>
    defaultSubdivisionModes(4, SUBDIVISIONS[0]!),
  );
  const [playing, setPlaying] = useState(false);
  // A task link may name the task's instrument. The provider outlives a visit, so the link's identity (task and
  // instrument) re-arms the preselection for each new task link, but never while a run is playing.
  const taskLink = location.pathname === "/practice/metronome" ? readPracticeToolContext(location.search) : null;
  const practiceInstrument = usePracticeInstrument(
    studentId ?? null,
    taskLink ? preferredToolInstrument(taskLink, studentId ?? null) : undefined,
    taskLink?.todo ? { key: `${taskLink.todo}|${taskLink.instrument ?? ""}`, locked: playing } : undefined,
  );
  const [activeBeat, setActiveBeat] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [saveToPractice, setSaveToPractice] = useState(Boolean(studentId));
  const [keepScreenAwake, setKeepScreenAwake] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [pendingSave, setPendingSave] = useState<PendingMetronomeSave | null>(null);
  const [assignment, setAssignment] = useState<MetronomeAssignment | null>(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [tapCount, setTapCount] = useState(0);

  const wakeLockSupported =
    typeof navigator !== "undefined" &&
    typeof (navigator as NavigatorWithMetronomeFeatures).wakeLock?.request === "function";

  const mediaRef = useRef<HTMLAudioElement | null>(null);
  const mediaUrlRef = useRef<string | null>(null);
  const mediaPatternRef = useRef<MetronomeAudioPattern | null>(null);
  const mediaFirstBarRef = useRef(0);
  const speedUpRef = useRef(speedUp);
  const pendingPatternRef = useRef<MetronomeAudioPattern | null>(null);
  const patternSwitchTimerRef = useRef<number | null>(null);
  const patternReplaceAttemptRef = useRef(0);
  const visualFrameRef = useRef<number | null>(null);
  const lastVisualBeatRef = useRef<number | null>(null);
  const lastElapsedUiAtRef = useRef(0);
  const wakeLockRef = useRef<ScreenWakeLockSentinel | null>(null);
  const playingRef = useRef(false);
  const bpmRef = useRef(bpm);
  const signatureRef = useRef(signature);
  const subdivisionRef = useRef(subdivision);
  const beatModesRef = useRef(beatModes);
  const subdivisionModesRef = useRef(subdivisionModes);
  const sessionRef = useRef<MetronomeSession | null>(null);
  const startGuardRef = useRef(false);
  const startAttemptRef = useRef(0);
  const saveToPracticeRef = useRef(saveToPractice);
  const tapTimesRef = useRef<number[]>([]);
  const assignmentRef = useRef<MetronomeAssignment | null>(null);
  const onUnmountPendingSaveRef = useRef(onUnmountPendingSave);
  const onUnmountPendingSaveClearedRef = useRef(onUnmountPendingSaveCleared);
  const startCommandRef = useRef<() => void>(() => undefined);
  const stopCommandRef = useRef<() => void>(() => undefined);

  const resetTapTempo = useCallback(() => {
    tapTimesRef.current = [];
    setTapCount(0);
  }, []);

  useEffect(() => {
    bpmRef.current = bpm;
  }, [bpm]);

  useEffect(() => {
    signatureRef.current = signature;
  }, [signature]);

  useEffect(() => {
    subdivisionRef.current = subdivision;
  }, [subdivision]);

  useEffect(() => {
    beatModesRef.current = beatModes;
  }, [beatModes]);

  useEffect(() => {
    subdivisionModesRef.current = subdivisionModes;
  }, [subdivisionModes]);

  useEffect(() => {
    saveToPracticeRef.current = saveToPractice;
  }, [saveToPractice]);

  useEffect(() => {
    assignmentRef.current = assignment;
  }, [assignment]);

  useEffect(() => {
    onUnmountPendingSaveRef.current = onUnmountPendingSave;
  }, [onUnmountPendingSave]);

  useEffect(() => {
    onUnmountPendingSaveClearedRef.current = onUnmountPendingSaveCleared;
  }, [onUnmountPendingSaveCleared]);

  function playbackPattern(): MetronomeAudioPattern {
    return {
      bpm: bpmRef.current,
      beats: signatureRef.current.beats,
      subdivision: {
        divisions: subdivisionRef.current.divisions,
        positions: [...subdivisionRef.current.positions],
      },
      beatModes: [...beatModesRef.current],
      subdivisionModes: subdivisionModesRef.current.map((row) => [...row]),
      speedUp: speedUpRef.current.enabled ? {
        increaseBpm: speedUpRef.current.increaseBpm,
        everyBars: speedUpRef.current.everyBars,
        targetBpm: speedUpRef.current.targetBpm,
      } : undefined,
    };
  }

  function createLoopUrl(pattern: MetronomeAudioPattern, firstBar = 0): string {
    const wav = createMetronomeLoopWavBytes(pattern, undefined, undefined, firstBar);
    const blob = new Blob([wav.buffer as ArrayBuffer], { type: "audio/wav" });
    return URL.createObjectURL(blob);
  }

  function currentPlaybackBpm(): number {
    const pattern = mediaPatternRef.current;
    const media = mediaRef.current;
    return pattern && media
      ? metronomeAudioPosition(pattern, media.currentTime, mediaFirstBarRef.current).bpm
      : bpmRef.current;
  }

  function configureMedia(media: HTMLAudioElement, pattern: MetronomeAudioPattern, firstBar = 0) {
    // Constant/target-tempo media can loop natively. A long speed-up track
    // continues at its next bar instead of repeating the opening slow bars.
    media.loop = !pattern.speedUp || metronomeBpmAtBar(pattern, firstBar) >= pattern.speedUp.targetBpm;
    media.preload = "auto";
    media.setAttribute("playsinline", "");
    if (!media.loop) {
      const { nextBar } = metronomeTrackLayout(pattern, undefined, firstBar);
      media.onended = () => {
        if (mediaRef.current === media && sessionRef.current) {
          const continuationAttempt = patternReplaceAttemptRef.current + 1;
          void replacePlaybackPattern(pattern, nextBar).then(async () => {
            if (patternReplaceAttemptRef.current === continuationAttempt && mediaRef.current === media && sessionRef.current) {
              await stop();
              setProblem((current) => current?.startsWith("The metronome stopped, but")
                ? current : "The metronome could not continue playing. Press play to restart.");
            }
          });
        }
      };
    }
  }

  function setAudioSessionType(type: "auto" | "playback") {
    if (typeof navigator === "undefined") return;
    const audioSession = (navigator as NavigatorWithMetronomeFeatures).audioSession;
    if (!audioSession) return;
    try {
      audioSession.type = type;
    } catch {
      // Experimental platform hint: failure must never stop the metronome.
    }
  }

  function updateMediaSessionMetadata(pattern: MetronomeAudioPattern) {
    if (
      typeof navigator === "undefined" ||
      !("mediaSession" in navigator) ||
      typeof MediaMetadata === "undefined"
    ) {
      return;
    }
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: "Metronome",
        artist: "Wirral Music Factory",
        album: `${pattern.bpm} BPM · ${signatureRef.current.label}`,
      });
    } catch {
      // Metadata/lock-screen integration is additive, never load-bearing.
    }
  }

  function setMediaSessionPlaybackState(state: MediaSessionPlaybackState) {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.playbackState = state;
    } catch {
      // Some engines expose Media Session only partially.
    }
  }

  function clearMediaSessionPresentation() {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = "none";
    } catch {
      // Best-effort OS integration only.
    }
  }

  function clearPatternSwitch() {
    if (patternSwitchTimerRef.current !== null) {
      window.clearTimeout(patternSwitchTimerRef.current);
      patternSwitchTimerRef.current = null;
    }
    pendingPatternRef.current = null;
  }

  function stopVisualScheduler(updateUi = true) {
    if (visualFrameRef.current !== null) {
      window.cancelAnimationFrame(visualFrameRef.current);
      visualFrameRef.current = null;
    }
    lastVisualBeatRef.current = null;
    if (updateUi) setActiveBeat(null);
  }

  function startVisualScheduler() {
    if (
      visualFrameRef.current !== null ||
      document.visibilityState !== "visible" ||
      !sessionRef.current
    ) {
      return;
    }

    visualFrameRef.current = window.requestAnimationFrame(function tick(frameTime) {
      visualFrameRef.current = null;
      const session = sessionRef.current;
      const media = mediaRef.current;
      const pattern = mediaPatternRef.current;
      if (!session || document.visibilityState !== "visible") return;

      if (media && pattern) {
        const { bpm: audibleBpm, beatIndex } = metronomeAudioPosition(pattern, media.currentTime, mediaFirstBarRef.current);
        setLiveBpm(audibleBpm);
        if (beatIndex !== lastVisualBeatRef.current) {
          lastVisualBeatRef.current = beatIndex;
          setActiveBeat(beatIndex);
        }
      }

      if (frameTime - lastElapsedUiAtRef.current >= ELAPSED_UI_INTERVAL_MS) {
        lastElapsedUiAtRef.current = frameTime;
        setElapsedSeconds(
          Math.max(0, (stoppedAtFrom(session) - session.startedAtMs) / 1000),
        );
      }

      visualFrameRef.current = window.requestAnimationFrame(tick);
    });
  }

  function stopPlaybackMedia() {
    clearPatternSwitch();
    patternReplaceAttemptRef.current += 1;
    const media = mediaRef.current;
    if (media) {
      media.onended = null;
      media.pause();
    }
    const url = mediaUrlRef.current;
    mediaRef.current = null;
    mediaUrlRef.current = null;
    mediaPatternRef.current = null;
    mediaFirstBarRef.current = 0;
    if (url) URL.revokeObjectURL(url);
    clearMediaSessionPresentation();
    setAudioSessionType("auto");
  }

  async function replacePlaybackPattern(pattern: MetronomeAudioPattern, firstBar = 0) {
    const previousMedia = mediaRef.current;
    if (!previousMedia || !sessionRef.current) return;
    const replaceAttempt = ++patternReplaceAttemptRef.current;

    let nextUrl: string;
    try {
      nextUrl = createLoopUrl(pattern, firstBar);
    } catch (cause: unknown) {
      if (replaceAttempt !== patternReplaceAttemptRef.current) return;
      setProblem(
        cause instanceof Error
          ? cause.message
          : "Your browser could not rebuild the metronome sound.",
      );
      return;
    }

    const previousUrl = mediaUrlRef.current;
    const nextMedia = new Audio();
    nextMedia.src = nextUrl;
    configureMedia(nextMedia, pattern, firstBar);

    try {
      // Stage the replacement independently. The current loop keeps playing
      // until the browser proves the replacement is allowed to start, which is
      // especially important when a visibility change tightens autoplay policy.
      await nextMedia.play();
      if (
        replaceAttempt !== patternReplaceAttemptRef.current ||
        !sessionRef.current ||
        mediaRef.current !== previousMedia
      ) {
        nextMedia.pause();
        URL.revokeObjectURL(nextUrl);
        return;
      }

      previousMedia.pause();
      previousMedia.onended = null;
      mediaRef.current = nextMedia;
      mediaUrlRef.current = nextUrl;
      mediaPatternRef.current = pattern;
      mediaFirstBarRef.current = firstBar;
      setLiveBpm(metronomeBpmAtBar(pattern, firstBar));
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      updateMediaSessionMetadata(pattern);
      setMediaSessionPlaybackState("playing");
    } catch (cause: unknown) {
      nextMedia.pause();
      URL.revokeObjectURL(nextUrl);
      if (replaceAttempt !== patternReplaceAttemptRef.current) return;
      pendingPatternRef.current = pattern;
      setProblem(
        cause instanceof Error
          ? `The metronome could not apply the new settings: ${cause.message}. Previous settings are still playing.`
          : "The metronome could not apply the new settings. Previous settings are still playing.",
      );
    }
  }

  /**
   * Foreground edits change state immediately, then replace the native media
   * loop on the next main beat. If the page is hidden before that boundary we
   * flush the replacement immediately so the old pattern is never stranded by
   * JavaScript suspension while background media keeps playing.
   */
  function queuePatternRefresh() {
    const media = mediaRef.current;
    const currentPattern = mediaPatternRef.current;
    if (!sessionRef.current || !media || !currentPattern) return;

    const nextPattern = playbackPattern();
    if (metronomeAudioPatternsEqual(nextPattern, currentPattern)) {
      clearPatternSwitch();
      return;
    }

    pendingPatternRef.current = nextPattern;
    if (patternSwitchTimerRef.current !== null) return;

    const delay = metronomeAudioPosition(currentPattern, media.currentTime, mediaFirstBarRef.current).nextBeatDelayMs;
    patternSwitchTimerRef.current = window.setTimeout(() => {
      patternSwitchTimerRef.current = null;
      const pending = pendingPatternRef.current;
      pendingPatternRef.current = null;
      if (pending && sessionRef.current) void replacePlaybackPattern(pending);
    }, delay);
  }

  function flushPatternRefresh() {
    if (!pendingPatternRef.current) return;
    if (patternSwitchTimerRef.current !== null) {
      window.clearTimeout(patternSwitchTimerRef.current);
      patternSwitchTimerRef.current = null;
    }
    const pending = pendingPatternRef.current;
    pendingPatternRef.current = null;
    if (sessionRef.current) void replacePlaybackPattern(pending);
  }

  async function releaseWakeLock() {
    const sentinel = wakeLockRef.current;
    wakeLockRef.current = null;
    if (!sentinel) return;
    try {
      await sentinel.release();
    } catch {
      // The browser may already have released it on visibility change.
    }
  }

  /** A task link is a preset, not merely navigation. */
  useEffect(() => {
    let live = true;
    const todoId = new URLSearchParams(location.search).get("todo");
    resetTapTempo();
    if (!studentId || location.pathname !== "/practice/metronome" || !todoId) {
      setAssignment(null);
      setAssignmentLoading(false);
      return () => {
        live = false;
      };
    }

    setAssignmentLoading(true);
    setProblem(null);
    get<{ todos: MetronomeAssignment[] }>(
      `/me/students/${encodeURIComponent(studentId)}/practice-todos`,
    )
      .then(({ todos }) => {
        if (!live) return;
        const todo = todos.find(
          (candidate) => candidate.id === todoId && candidate.metronome !== null,
        );
        if (!todo?.metronome) {
          setAssignment(null);
          setProblem("That metronome practice task is no longer available.");
          return;
        }
        const nextSignature = TIME_SIGNATURES.find(
          (candidate) => candidate.label === todo.metronome!.signature,
        );
        if (!nextSignature) {
          setProblem(
            "That metronome preset uses a time signature this app cannot play.",
          );
          return;
        }

        const nextBpm = clampMetronomeBpm(todo.metronome.bpm);
        const nextSpeedUp = todo.metronome.speedUp
          ? { ...todo.metronome.speedUp, enabled: true }
          : defaultSpeedUp(nextBpm);
        speedUpRef.current = nextSpeedUp;
        setSpeedUp(nextSpeedUp);
        resetTapTempo();
        const nextBeatModes = [...todo.metronome.beatModes];
        const nextSubdivision = SUBDIVISIONS.find(
          (candidate) => candidate.id === todo.metronome!.subdivision?.id,
        ) ?? SUBDIVISIONS[0]!;
        const nextSubdivisionModes = todo.metronome.subdivision
          ? todo.metronome.subdivision.modes.map((row) => [...row])
          : defaultSubdivisionModes(nextSignature.beats, nextSubdivision);
        bpmRef.current = nextBpm;
        signatureRef.current = nextSignature;
        subdivisionRef.current = nextSubdivision;
        beatModesRef.current = nextBeatModes;
        subdivisionModesRef.current = nextSubdivisionModes;

        setAssignment(todo);
        setBpm(nextBpm);
        setSignature(nextSignature);
        setBeatModes(nextBeatModes);
        setSubdivision(nextSubdivision);
        setSubdivisionModes(nextSubdivisionModes);
        setActiveBeat(null);
        queuePatternRefresh();
        setStatus(
          `Loaded “${todo.title}”. Practice here counts against this task.`,
        );
      })
      .catch((cause: unknown) => {
        if (!live) return;
        setProblem(
          cause instanceof Error
            ? cause.message
            : "Could not load the metronome assignment.",
        );
      })
      .finally(() => {
        if (live) setAssignmentLoading(false);
      });

    return () => {
      live = false;
    };
  }, [location.pathname, location.search, studentId, resetTapTempo]);

  useEffect(() => {
    return () => {
      const session = sessionRef.current;
      const endBpm = currentPlaybackBpm();
      startAttemptRef.current += 1;
      startGuardRef.current = false;
      playingRef.current = false;
      sessionRef.current = null;
      stopVisualScheduler(false);
      stopPlaybackMedia();
      void releaseWakeLock();
      if (!session) return;
      const stoppedAtMs = stoppedAtFrom(session);
      const measuredSeconds = Math.max(
        0,
        (stoppedAtMs - session.startedAtMs) / 1000,
      );
      if (!practicePath || !shouldSaveMetronomeSession(saveToPracticeRef.current, measuredSeconds)) {
        return;
      }
      const assigned = assignmentRef.current;
      const pending: PendingMetronomeSave = {
        practicePath,
        session: { ...session },
        stoppedAtMs,
        endBpm,
        assigned: assigned && studentId ? { studentId, todoId: assigned.id } : undefined,
        assignmentTitle: assigned?.title,
      };

      // Persist before starting the fire-and-forget request. If navigation or a
      // family-member switch unmounts this provider and the request fails, the
      // exact frozen interval remains recoverable instead of disappearing.
      onUnmountPendingSaveRef.current?.(pending);
      void savePendingMetronomeInterval(pending)
        .then(() => onUnmountPendingSaveClearedRef.current?.())
        .catch(() => undefined);
    };
  }, [practicePath, studentId]);

  async function start() {
    if (saveToPractice && practiceInstrument.blocked) {
      setProblem(practiceInstrument.problem);
      return;
    }
    if (!claimMetronomeStart(startGuardRef, sessionRef.current)) return;

    const attempt = startAttemptRef.current + 1;
    startAttemptRef.current = attempt;
    setProblem(null);
    setStatus(null);
    resetTapTempo();

    let media: HTMLAudioElement | null = null;
    let url: string | null = null;
    try {
      const pattern = playbackPattern();
      url = createLoopUrl(pattern);
      media = new Audio();
      media.src = url;
      configureMedia(media, pattern);
      mediaRef.current = media;
      mediaUrlRef.current = url;
      mediaPatternRef.current = pattern;
      mediaFirstBarRef.current = 0;

      // Safari/iOS can use this experimental hint to classify the metronome as
      // deliberate playback rather than ambient Web Audio. Other browsers just
      // ignore it; the ordinary <audio> element remains the primary mechanism.
      setAudioSessionType("playback");
      await media.play();
      if (attempt !== startAttemptRef.current) {
        media.pause();
        if (mediaRef.current === media) {
          mediaRef.current = null;
          mediaUrlRef.current = null;
          mediaPatternRef.current = null;
        }
        URL.revokeObjectURL(url);
        return;
      }

      const startedAtMs = Date.now();
      const startedAtPerf = performance.now();
      sessionRef.current = {
        instrument: practiceInstrument.value || undefined,
        startedAtMs,
        startedAtPerf,
        startBpm: pattern.bpm,
        signature: signatureRef.current.label,
      };
      playingRef.current = true;
      setElapsedSeconds(0);
      setActiveBeat(0);
      setPlaying(true);
      setLiveBpm(pattern.bpm);
      updateMediaSessionMetadata(pattern);
      setMediaSessionPlaybackState("playing");

      // A slow/permission-gated `play()` can leave enough time for somebody to
      // edit BPM, subdivision or accents before it resolves. Reconcile the
      // native loop now that a session exists; equality keeps ordinary starts
      // from rebuilding an identical bar.
      queuePatternRefresh();
    } catch (cause: unknown) {
      if (media) media.pause();
      if (url) URL.revokeObjectURL(url);
      if (mediaRef.current === media) {
        mediaRef.current = null;
        mediaUrlRef.current = null;
        mediaPatternRef.current = null;
      }
      clearMediaSessionPresentation();
      setAudioSessionType("auto");
      if (attempt !== startAttemptRef.current) return;
      stopVisualScheduler();
      setPlaying(false);
      setLiveBpm(null);
      playingRef.current = false;
      sessionRef.current = null;
      setProblem(
        cause instanceof Error
          ? cause.message
          : "Your browser could not start the metronome.",
      );
    } finally {
      if (attempt === startAttemptRef.current) startGuardRef.current = false;
    }
  }

  async function saveStoppedInterval(pending: PendingMetronomeSave) {
    setSaving(true);
    setProblem(null);
    try {
      const result = await savePendingMetronomeInterval(pending);
      setPendingSave(null);
      setStatus(
        result.clamped
          ? "Metronome practice saved at the maximum session length."
          : pending.assignmentTitle
            ? `Practice saved against “${pending.assignmentTitle}”.`
            : "Metronome practice saved to your practice history.",
      );
    } catch (cause: unknown) {
      if (cause instanceof ApiError && (cause.code === "practice_overlap" || cause.code === "practice_deleted")) {
        setPendingSave(null);
        setStatus(`Metronome stopped. ${cause.message}`);
      } else {
        // Keep the exact frozen interval. A retry must not use the current BPM,
        // a later stop time or a different selected assignment.
        setPendingSave(pending);
        setProblem(
          cause instanceof Error
            ? `The metronome stopped, but the practice record was not saved: ${cause.message}`
            : "The metronome stopped, but the practice record was not saved.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  async function retrySave() {
    if (!pendingSave || saving) return;
    await saveStoppedInterval(pendingSave);
  }

  function discardPendingSave() {
    if (saving || !pendingSave) return;
    setPendingSave(null);
    setProblem(null);
    setStatus("Unsaved metronome session discarded.");
  }

  async function stop() {
    if (startGuardRef.current && sessionRef.current === null) {
      startAttemptRef.current += 1;
      startGuardRef.current = false;
      playingRef.current = false;
      stopVisualScheduler();
      stopPlaybackMedia();
      setPlaying(false);
      setStatus("Metronome stopped.");
      return;
    }

    const session = sessionRef.current;
    if (!session) return;

    const stoppedAtMs = stoppedAtFrom(session);
    const endBpm = currentPlaybackBpm();
    const measuredSeconds = Math.max(
      0,
      (stoppedAtMs - session.startedAtMs) / 1000,
    );
    stopVisualScheduler();
    stopPlaybackMedia();
    setElapsedSeconds(measuredSeconds);
    setPlaying(false);
    setLiveBpm(null);
    playingRef.current = false;
    sessionRef.current = null;
    practiceInstrument.reset();

    if (!practicePath) {
      setStatus("Metronome stopped.");
      return;
    }

    if (!shouldSaveMetronomeSession(saveToPractice, measuredSeconds)) {
      setPendingSave(null);
      setStatus(
        saveToPractice
          ? "Metronome stopped. Sessions under one minute are not added to practice history."
          : "Metronome stopped. This session was not added to practice history.",
      );
      return;
    }

    const assigned = assignmentRef.current;
    const pending: PendingMetronomeSave = {
      practicePath,
      session: { ...session },
      stoppedAtMs,
      endBpm,
      assigned: assigned && studentId ? { studentId, todoId: assigned.id } : undefined,
      assignmentTitle: assigned?.title,
    };
    await saveStoppedInterval(pending);
  }

  function toggle() {
    void (sessionRef.current || startGuardRef.current ? stop() : start());
  }

  function applyBpm(next: number) {
    const nextBpm = clampMetronomeBpm(next);
    bpmRef.current = nextBpm;
    setBpm(nextBpm);
    if (speedUpRef.current.targetBpm < nextBpm) {
      speedUpRef.current = { ...speedUpRef.current, targetBpm: nextBpm };
      setSpeedUp(speedUpRef.current);
    }
    queuePatternRefresh();
  }

  function changeSpeedUp(change: Partial<SpeedUpSettings>) {
    const next = { ...speedUpRef.current, ...change };
    const bounded = (value: number, min: number, max: number, fallback: number) =>
      Number.isFinite(value) ? Math.min(max, Math.max(min, Math.round(value))) : fallback;
    next.increaseBpm = bounded(next.increaseBpm, 1, 100, 2);
    next.everyBars = bounded(next.everyBars, 1, 1000, 4);
    next.targetBpm = bounded(next.targetBpm, bpmRef.current, MAX_BPM, bpmRef.current);
    speedUpRef.current = next;
    setSpeedUp(next);
    resetTapTempo();
    queuePatternRefresh();
  }

  function changeBpm(next: number) {
    resetTapTempo();
    applyBpm(next);
  }

  function tapTempo() {
    const now = performance.now();
    const taps = tapTimesRef.current;
    const previous = taps.at(-1);
    const averageInterval = taps.length >= 2
      ? (previous! - taps[0]!) / (taps.length - 1)
      : null;
    // Allow a slow second tap before a rhythm is known. Once established,
    // a pause relative to that rhythm starts a new sequence immediately.
    const resetAfter = averageInterval === null
      ? TAP_RESET_MS
      : Math.min(TAP_RESET_MS, averageInterval * 2.5);
    tapTimesRef.current =
      previous === undefined || now - previous > resetAfter
        ? [now]
        : [...taps.slice(-4), now];
    setTapCount(tapTimesRef.current.length);
    const nextBpm = metronomeBpmFromTaps(tapTimesRef.current);
    if (nextBpm !== null) applyBpm(nextBpm);
  }

  function chooseSignature(next: TimeSignature) {
    const nextBeatModes = defaultMetronomeBeatModes(next.beats);
    const nextSubdivisionModes = defaultSubdivisionModes(
      next.beats,
      subdivisionRef.current,
    );
    signatureRef.current = next;
    beatModesRef.current = nextBeatModes;
    subdivisionModesRef.current = nextSubdivisionModes;
    setSignature(next);
    setBeatModes(nextBeatModes);
    setSubdivisionModes(nextSubdivisionModes);
    setActiveBeat(null);
    queuePatternRefresh();
  }

  function chooseSubdivision(next: Subdivision) {
    const nextModes = defaultSubdivisionModes(signatureRef.current.beats, next);
    subdivisionRef.current = next;
    subdivisionModesRef.current = nextModes;
    setSubdivision(next);
    setSubdivisionModes(nextModes);
    queuePatternRefresh();
  }

  function cycleBeat(index: number) {
    const next = beatModesRef.current.map((mode, beatIndex) =>
      beatIndex === index ? nextMetronomeBeatMode(mode) : mode,
    );
    beatModesRef.current = next;
    setBeatModes(next);
    queuePatternRefresh();
  }

  function cycleSubdivision(beatIndex: number, extraIndex: number) {
    const next = subdivisionModesRef.current.map((row, rowIndex) =>
      rowIndex === beatIndex
        ? row.map((mode, index) =>
            index === extraIndex ? nextMetronomeBeatMode(mode) : mode,
          )
        : row,
    );
    subdivisionModesRef.current = next;
    setSubdivisionModes(next);
    queuePatternRefresh();
  }

  // Visual work is deliberately foreground-only. Native looping media owns the
  // audible clock, so hiding/locking the phone can stop animation work entirely.
  useEffect(() => {
    if (!playing) {
      stopVisualScheduler();
      return;
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        flushPatternRefresh();
        startVisualScheduler();
      } else {
        stopVisualScheduler();
        flushPatternRefresh();
      }
    };

    onVisibilityChange();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      stopVisualScheduler(false);
    };
  }, [playing]);

  // Wake lock is explicitly optional. It exists only for someone who wants to
  // watch the beat display; background audio does not depend on it.
  useEffect(() => {
    const wakeLock = (navigator as NavigatorWithMetronomeFeatures).wakeLock;
    if (!wakeLockSupported || !wakeLock || !playing || !keepScreenAwake) {
      void releaseWakeLock();
      return;
    }

    let cancelled = false;
    const acquire = async () => {
      if (
        cancelled ||
        !playingRef.current ||
        !keepScreenAwake ||
        document.visibilityState !== "visible" ||
        wakeLockRef.current
      ) {
        return;
      }
      try {
        const sentinel = await wakeLock.request("screen");
        if (cancelled || !playingRef.current || document.visibilityState !== "visible") {
          await sentinel.release();
          return;
        }
        wakeLockRef.current = sentinel;
        sentinel.addEventListener("release", () => {
          if (wakeLockRef.current === sentinel) wakeLockRef.current = null;
        });
      } catch {
        // Low battery/policy can reject a wake lock. Audio should carry on.
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void acquire();
      } else {
        void releaseWakeLock();
      }
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      void releaseWakeLock();
    };
  }, [playing, keepScreenAwake, wakeLockSupported]);

  startCommandRef.current = () => {
    const media = mediaRef.current;
    if (sessionRef.current && media?.paused) {
      setAudioSessionType("playback");
      void media.play().then(
        () => setMediaSessionPlaybackState("playing"),
        () => undefined,
      );
      return;
    }
    void start();
  };
  stopCommandRef.current = () => void stop();

  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const mediaSession = navigator.mediaSession;
    try {
      mediaSession.setActionHandler("play", () => startCommandRef.current());
      mediaSession.setActionHandler("pause", () => stopCommandRef.current());
      mediaSession.setActionHandler("stop", () => stopCommandRef.current());
    } catch {
      // Some implementations reject unsupported action names individually.
    }

    return () => {
      try {
        mediaSession.setActionHandler("play", null);
        mediaSession.setActionHandler("pause", null);
        mediaSession.setActionHandler("stop", null);
        mediaSession.metadata = null;
        mediaSession.playbackState = "none";
      } catch {
        // Best-effort OS integration only.
      }
    };
  }, []);

  const value: MetronomeContextValue = {
    bpm,
    liveBpm,
    speedUp,
    changeSpeedUp,
    signature,
    subdivision,
    beatModes,
    subdivisionModes,
    playing,
    activeBeat,
    elapsedSeconds,
    canSavePractice: Boolean(practicePath),
    practiceInstrument,
    saveToPractice,
    keepScreenAwake,
    wakeLockSupported,
    saving,
    status,
    problem,
    pendingSave,
    tempoTerm: metronomeTempoTerm(bpm),
    assignment,
    assignmentLoading,
    changeBpm,
    tapTempo,
    tapCount,
    resetTapTempo,
    chooseSignature,
    chooseSubdivision,
    cycleBeat,
    cycleSubdivision,
    toggle,
    start,
    stop,
    retrySave,
    discardPendingSave,
    setSaveToPractice,
    setKeepScreenAwake,
  };

  return (
    <MetronomeContext.Provider value={value}>
      {children}
    </MetronomeContext.Provider>
  );
}

function MetronomeSpeedUpControls() {
  const metronome = useMetronome();
  const prefix = useId();
  const { speedUp, changeSpeedUp } = metronome;
  return <div className="metronome-speed-up">
    <div className="metronome-speed-toggle">
      <label htmlFor={`${prefix}-speed-up`}><strong>Speed up</strong></label>
      <select id={`${prefix}-speed-up`} value={speedUp.enabled ? "on" : "off"}
        onChange={(event) => changeSpeedUp({ enabled: event.target.value === "on" })}>
        <option value="off">Off</option>
        <option value="on">On</option>
      </select>
    </div>
    {speedUp.enabled && <fieldset className="metronome-speed-settings">
      <legend>Speed-up settings</legend>
      <div className="metronome-speed-grid">
        <div><label htmlFor={`${prefix}-increase`}><strong>Increase by</strong></label>
          <span><PlaybackNumberInput id={`${prefix}-increase`} value={speedUp.increaseBpm} min={1} max={100}
            onCommit={(increaseBpm) => changeSpeedUp({ increaseBpm })} /> BPM</span>
        </div>
        <div><label htmlFor={`${prefix}-bars`}><strong>Every</strong></label>
          <span><PlaybackNumberInput id={`${prefix}-bars`} value={speedUp.everyBars} min={1} max={1000}
            onCommit={(everyBars) => changeSpeedUp({ everyBars })} /> bars</span>
        </div>
        <div><label htmlFor={`${prefix}-target`}><strong>Target speed</strong></label>
          <span><PlaybackNumberInput id={`${prefix}-target`} value={speedUp.targetBpm} min={metronome.bpm} max={MAX_BPM}
            onCommit={(targetBpm) => changeSpeedUp({ targetBpm })} /> BPM</span>
        </div>
      </div>
      <p className="small muted">Increase by {speedUp.increaseBpm} BPM every {speedUp.everyBars} {speedUp.everyBars === 1 ? "bar" : "bars"}, then keep playing at {speedUp.targetBpm} BPM.
        {metronome.playing && " Changing the tempo or these settings restarts the bar count."}
      </p>
      {metronome.playing && <p className="metronome-live-bpm" role="status">
        Now {metronome.liveBpm ?? metronome.bpm} BPM
        {(metronome.liveBpm ?? metronome.bpm) >= speedUp.targetBpm && " — Target reached"}
      </p>}
    </fieldset>}
  </div>;
}

export function MetronomeControls({ compact = false, tapButtonRef }: {
  compact?: boolean;
  tapButtonRef?: RefObject<HTMLButtonElement>;
}) {
  const metronome = useMetronome();
  const extras = metronome.subdivision.positions.filter(
    (position) => position !== 0,
  );

  useEffect(() => {
    if (compact) return;
    return metronome.resetTapTempo;
  }, [compact, metronome.resetTapTempo]);

  if (compact) {
    return (
      <div className="metronome-mini-controls" aria-label="Metronome quick controls">
        <strong className="tabular">{metronome.bpm} BPM</strong>
        <span className="small muted">{metronome.subdivision.shortLabel}</span>
        <button
          type="button"
          className="metronome-flat-step"
          aria-label="Decrease tempo"
          onClick={() => metronome.changeBpm(metronome.bpm - 1)}
        >
          −
        </button>
        <button
          type="button"
          className="metronome-mini-play"
          aria-label={metronome.playing ? "Stop metronome" : "Start metronome"}
          onClick={metronome.toggle}
        >
          {metronome.playing ? "■" : "▶"}
        </button>
        <button
          type="button"
          className="metronome-flat-step"
          aria-label="Increase tempo"
          onClick={() => metronome.changeBpm(metronome.bpm + 1)}
        >
          +
        </button>
      </div>
    );
  }

  return (
    <div className="metronome-controls">
      {metronome.assignmentLoading && (
        <div className="notice">Loading your tutor&rsquo;s metronome settings…</div>
      )}
      {metronome.assignment && (
        <div
          className="notice metronome-assignment"
          aria-label="Assigned metronome practice"
        >
          <strong>{metronome.assignment.title}</strong>
          <div className="small muted">
            {metronome.assignment.description ??
              "Your tutor has set these metronome settings for practice."}
            {metronome.assignment.suggestedMinutes != null &&
              ` · Aim for ${metronome.assignment.suggestedMinutes} min/day`}
          </div>
        </div>
      )}

      <section
        className="metronome-section metronome-tempo-section"
        aria-labelledby="metronome-tempo-heading"
      >
        <div className="metronome-section-heading">
          <div>
            <h2 id="metronome-tempo-heading">Tempo</h2>
            <p className="small muted">
              Tap to find your tempo, then use the play button to start or stop.
            </p>
          </div>
          <div className="metronome-tempo-primary-controls">
            <button
              type="button"
              className={`metronome-play metronome-play-inline${metronome.playing ? " is-playing" : ""}`}
              aria-pressed={metronome.playing}
              aria-label={metronome.playing ? "Stop metronome" : "Start metronome"}
              disabled={metronome.saving}
              onClick={metronome.toggle}
            >
              <span aria-hidden="true">{metronome.playing ? "■" : "▶"}</span>
            </button>
            <span className="metronome-session-time tabular">
              {secondsAsClock(metronome.elapsedSeconds)}
            </span>
          </div>
        </div>
        <div className="metronome-tempo-row">
          <button
            type="button"
            className="metronome-flat-step"
            aria-label="Decrease tempo by one BPM"
            onClick={() => metronome.changeBpm(metronome.bpm - 1)}
          >
            −
          </button>
          <div className="metronome-bpm" aria-live="polite">
            <span className="metronome-bpm-number tabular">{metronome.bpm}</span>
            <span className="metronome-bpm-label">BPM</span>
          </div>
          <button
            type="button"
            className="metronome-flat-step"
            aria-label="Increase tempo by one BPM"
            onClick={() => metronome.changeBpm(metronome.bpm + 1)}
          >
            +
          </button>
        </div>
        <input
          type="range"
          min={MIN_BPM}
          max={MAX_BPM}
          value={metronome.bpm}
          aria-label="Tempo in beats per minute"
          onChange={(event) =>
            metronome.changeBpm(Number(event.target.value))
          }
        />
        <div className="metronome-tap-area">
          <button
            ref={tapButtonRef}
            type="button"
            className="quiet metronome-tap"
            aria-label="Tap tempo"
            aria-describedby="metronome-tap-help"
            onPointerDown={(event) => {
              if (event.button > 2 || (event.pointerType === "touch" && !event.isPrimary)) {
                return;
              }
              event.preventDefault();
              event.currentTarget.focus({ preventScroll: true });
              metronome.tapTempo();
            }}
            onKeyDown={(event) => {
              if (event.code !== "Space" && event.key !== "Enter") return;
              if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
              event.preventDefault();
              event.stopPropagation();
              if (!event.repeat) metronome.tapTempo();
            }}
            onKeyUp={(event) => {
              // Some browsers activate a focused button on Space release,
              // even when its keydown was handled. Count only the press.
              if (event.code === "Space") event.preventDefault();
            }}
            onClick={(event) => {
              // Pointer input is measured on press. Keep native assistive
              // activation without counting the later pointer click twice.
              if (event.detail === 0) metronome.tapTempo();
            }}
            onAuxClick={(event) => event.preventDefault()}
            onContextMenu={(event) => event.preventDefault()}
          >
            Tap tempo
          </button>
          <p id="metronome-tap-help" className="small muted">
            Tap here, click any mouse button here, or press Space in time with the beat.
          </p>
          <span className="small metronome-tap-feedback" role="status">
            {metronome.tapCount === 0
              ? "Tap at least twice to set the tempo."
              : metronome.tapCount === 1
                ? "Tap again to set the tempo."
                : "Tempo set. Keep tapping to refine it."}
          </span>
        </div>
        <div className="metronome-tempo-term" aria-live="polite">
          <strong>{metronome.tempoTerm.name}</strong>
          <span>{metronome.tempoTerm.description}</span>
        </div>
      </section>

      <section className="metronome-section metronome-control-section">
        <div className="metronome-options">
          <PracticeToolContextNote choice={metronome.practiceInstrument}/>
          {metronome.canSavePractice&&<PracticeInstrumentPicker choice={metronome.practiceInstrument} disabled={metronome.playing}/>}
          {metronome.canSavePractice && <label className="metronome-save-option">
            <input
              type="checkbox"
              checked={metronome.saveToPractice}
              disabled={metronome.playing}
              onChange={(event) =>
                metronome.setSaveToPractice(event.target.checked)
              }
            />
            <span>
              <strong>
                {metronome.assignment
                  ? "Save against this practice task"
                  : "Save to practice history"}
              </strong>
              <span className="small muted">
                Sessions of at least one minute are logged when stopped.
              </span>
            </span>
          </label>}
          {metronome.wakeLockSupported && (
            <label className="metronome-save-option">
              <input
                type="checkbox"
                checked={metronome.keepScreenAwake}
                onChange={(event) =>
                  metronome.setKeepScreenAwake(event.target.checked)
                }
              />
              <span>
                <strong>Keep screen awake</strong>
                <span className="small muted">
                  Optional for watching the beat display. Uses more battery; audio does not depend on it.
                </span>
              </span>
            </label>
          )}
        </div>
        <MetronomeSpeedUpControls />
      </section>

      <section
        className="metronome-section"
        aria-labelledby="metronome-signature-heading"
      >
        <h2 id="metronome-signature-heading">Time signature</h2>
        <div className="metronome-choice-grid compact">
          {TIME_SIGNATURES.map((item) => (
            <button
              key={item.label}
              type="button"
              className={
                item.label === metronome.signature.label ? "is-selected" : "quiet"
              }
              aria-pressed={item.label === metronome.signature.label}
              onClick={() => metronome.chooseSignature(item)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </section>

      <section
        className="metronome-section"
        aria-labelledby="metronome-subdivision-heading"
      >
        <h2 id="metronome-subdivision-heading">Subdivision</h2>
        <div className="metronome-choice-grid subdivisions">
          {SUBDIVISIONS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={
                item.id === metronome.subdivision.id ? "is-selected" : "quiet"
              }
              aria-pressed={item.id === metronome.subdivision.id}
              onClick={() => metronome.chooseSubdivision(item)}
            >
              {item.shortLabel}
            </button>
          ))}
        </div>
      </section>

      <section
        className="metronome-section metronome-beat-section"
        aria-labelledby="metronome-beats-heading"
      >
        <div className="metronome-section-heading">
          <div>
            <h2 id="metronome-beats-heading">Beats</h2>
            <p className="small muted">
              Tap any circle to cycle normal → accent → silent.
            </p>
          </div>
        </div>
        <div
          className="metronome-beat-grid"
          style={{
            gridTemplateColumns: `repeat(${metronome.signature.beats}, minmax(54px, 1fr))`,
          }}
        >
          {metronome.beatModes.map((mode, beatIndex) => (
            <div
              className="metronome-beat-column"
              key={`${metronome.signature.label}-${beatIndex}`}
            >
              <button
                type="button"
                className={`metronome-beat is-${mode}${
                  metronome.activeBeat === beatIndex ? " is-current" : ""
                }`}
                aria-label={`Beat ${beatIndex + 1}, ${beatModeLabel(mode)}`}
                onClick={() => metronome.cycleBeat(beatIndex)}
              >
                <span>{beatIndex + 1}</span>
              </button>
              {extras.length > 0 && (
                <div
                  className="metronome-subdivision-markers"
                  aria-label={`Subdivision clicks after beat ${beatIndex + 1}`}
                >
                  {extras.map((position, extraIndex) => {
                    const extraMode =
                      metronome.subdivisionModes[beatIndex]?.[extraIndex] ??
                      "normal";
                    return (
                      <button
                        key={`${position}-${extraIndex}`}
                        type="button"
                        className={`metronome-subdivision-dot is-${extraMode}`}
                        aria-label={`Beat ${beatIndex + 1} subdivision ${
                          extraIndex + 1
                        }, ${beatModeLabel(extraMode)}`}
                        onClick={() =>
                          metronome.cycleSubdivision(beatIndex, extraIndex)
                        }
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {metronome.problem && (
        <div className="error" role="alert">
          {metronome.problem}
          {metronome.pendingSave && (
            <div>
              <button
                type="button"
                className="quiet"
                disabled={metronome.saving}
                onClick={() => void metronome.retrySave()}
              >
                {metronome.saving ? "Retrying…" : "Retry saving this session"}
              </button>{" "}
              <button
                type="button"
                className="quiet"
                disabled={metronome.saving}
                onClick={metronome.discardPendingSave}
              >
                Discard unsaved session
              </button>
            </div>
          )}
        </div>
      )}
      {metronome.status && (
        <div className="notice" role="status">
          {metronome.status}
        </div>
      )}
    </div>
  );
}

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute("hidden"));
}

export function useMetronomeSpaceShortcut({ enabled, tapTempo }: {
  enabled: boolean;
  tapTempo: boolean;
}) {
  const metronome = useMetronome();
  const toggleRef = useRef(metronome.toggle);
  const tapTempoRef = useRef(metronome.tapTempo);

  useEffect(() => {
    toggleRef.current = metronome.toggle;
    tapTempoRef.current = metronome.tapTempo;
  }, [metronome.toggle, metronome.tapTempo]);

  useEffect(() => {
    if (!enabled) return;
    // Native controls, disclosures and focusable widgets retain their Space
    // activation via isTypingTarget; only the surrounding surface is captured.
    const captureSpace = tapTempo;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.code !== "Space" ||
        event.altKey || event.ctrlKey || event.metaKey || event.shiftKey ||
        event.defaultPrevented ||
        isTypingTarget(event.target)
      ) {
        return;
      }
      event.preventDefault();
      if (captureSpace) event.stopPropagation();
      if (event.repeat) return;
      if (captureSpace) {
        tapTempoRef.current();
      } else {
        toggleRef.current();
      }
    };
    window.addEventListener("keydown", onKeyDown, captureSpace);
    return () => window.removeEventListener("keydown", onKeyDown, captureSpace);
  }, [enabled, tapTempo]);
}

export function PracticeMetronomeShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const metronome = useMetronome();
  const [open, setOpen] = useState(false);
  const onDrumAssessment = pathname === "/practice/drums";
  const inPractice = isPracticePath(pathname) && !onDrumAssessment;
  const onMetronomePage = pathname === "/practice/metronome";
  const drawerRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const restoreFocusRef = useRef(false);

  useMetronomeSpaceShortcut({ enabled: inPractice, tapTempo: onMetronomePage || open });

  // This tool owns its own sample-clock metronome. A continuing click from
  // the general practice player would contaminate microphone measurements.
  useEffect(() => {
    if (onDrumAssessment && metronome.playing) void metronome.stop();
  }, [onDrumAssessment, metronome.playing, metronome.stop]);

  useEffect(() => {
    if (onMetronomePage || !inPractice) setOpen(false);
  }, [inPractice, onMetronomePage]);

  useLayoutEffect(() => {
    if (open) {
      const tap = drawerRef.current?.querySelector<HTMLButtonElement>(".metronome-tap");
      (tap ?? closeButtonRef.current)?.focus();
      return;
    }
    if (!restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    const usable = (element: HTMLElement | null | undefined) =>
      Boolean(element?.isConnected && element.getClientRects().length > 0 && !element.hasAttribute("disabled"));
    const original = returnFocusRef.current;
    const currentTrigger = document.querySelector<HTMLElement>(
      ".practice-metronome-launcher, .practice-metronome-mini .practice-metronome-expand",
    );
    if (usable(original)) original?.focus();
    else if (usable(currentTrigger)) currentTrigger?.focus();
  }, [open]);

  function openDrawer(trigger: HTMLElement) {
    returnFocusRef.current = trigger;
    setOpen(true);
  }

  function closeDrawer(restoreFocus = true) {
    restoreFocusRef.current = restoreFocus;
    setOpen(false);
  }

  function onDrawerKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== "Tab" || !drawerRef.current) return;

    const focusable = focusableElements(drawerRef.current);
    if (focusable.length === 0) {
      event.preventDefault();
      drawerRef.current.focus();
      return;
    }

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <>
      {children}

      {inPractice && !onMetronomePage && !open && !metronome.playing && (
        <button
          type="button"
          className="practice-metronome-launcher"
          onClick={(event) => openDrawer(event.currentTarget)}
        >
          Metronome
        </button>
      )}

      {!open && metronome.playing && !onMetronomePage && (
        <div className="practice-metronome-mini" data-bottom-obstruction="dock">
          <MetronomeControls compact />
          {/* Always actionable: outside Practice this returns the student to
              the full controls rather than presenting an inert disabled
              button, which is unfocusable and never announces its label. */}
          <button
            type="button"
            className="quiet practice-metronome-expand"
            onClick={(event) =>
              inPractice
                ? openDrawer(event.currentTarget)
                : navigate("/practice/metronome")
            }
            aria-label={
              inPractice
                ? "Open full metronome controls"
                : "Return to Practice for full metronome controls"
            }
          >
            {inPractice ? "Open" : "Practice"}
          </button>
        </div>
      )}

      {inPractice && open && (
        <div className="practice-metronome-drawer-layer">
          <div
            className="practice-metronome-backdrop"
            aria-hidden="true"
            onClick={() => closeDrawer()}
          />
          <aside
            ref={drawerRef}
            className="practice-metronome-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="practice-metronome-dialog-title"
            tabIndex={-1}
            onKeyDown={onDrawerKeyDown}
          >
            <div className="practice-metronome-drawer-heading">
              <div>
                <strong id="practice-metronome-dialog-title">Metronome</strong>
                <span className="small muted">
                  Keeps playing while you practise.
                </span>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                className="quiet"
                onClick={() => closeDrawer()}
              >
                Close
              </button>
            </div>
            <MetronomeControls />
          </aside>
        </div>
      )}
    </>
  );
}

export function Metronome() {
  return (
    <div className="metronome-page">
      <div className="metronome-heading-row">
        <div>
          <h1>Metronome</h1>
          <p className="muted">
            Set your pulse, subdivision and accents, then keep practising anywhere
            in Practice.
          </p>
        </div>
      </div>
      <MetronomeControls />
    </div>
  );
}
